import { Users, Inbox, CalendarCheck, FileSignature, UserCheck, PoundSterling, Receipt, Repeat, BarChart3 } from "lucide-react";
import { useLoad, Loading, EmptyState, Panel, gbp, STAGE_LABELS, STAGE_ORDER } from "./shared";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <Icon className="h-4 w-4 text-indigo-500" />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const Funnel = ({ counts, total }) => (
  <div className="p-5 space-y-2.5" data-testid="commerce-stage-funnel">
    {STAGE_ORDER.map(s => {
      const n = counts[s] || 0;
      return (
        <div key={s} className="flex items-center gap-3 text-sm">
          <span className="w-36 text-slate-600 shrink-0">{STAGE_LABELS[s]}</span>
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${total ? (n / total) * 100 : 0}%` }} />
          </div>
          <span className="w-8 text-right font-medium text-slate-900">{n}</span>
        </div>
      );
    })}
  </div>
);

const Sources = ({ sources, total }) => (
  <div className="p-5 space-y-3" data-testid="commerce-sources">
    {sources.map(s => (
      <div key={s.source} className="flex items-center justify-between text-sm">
        <span className="text-slate-700 capitalize">{s.source}</span>
        <span className="text-slate-500">{s.count} <span className="text-slate-400">({Math.round((s.count / total) * 100)}%)</span></span>
      </div>
    ))}
  </div>
);

export const CommerceOverview = ({ call }) => {
  const { data, loading } = useLoad(call, "/overview");
  if (loading || !data) return <Loading />;
  if (!data.has_data) {
    return <EmptyState testid="commerce-overview-empty" icon={BarChart3} title="No Commerce activity yet"
      text="Metrics will appear once the first enquiry, booking or playbook download arrives from the public Commerce website." />;
  }
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  return (
    <div className="space-y-6" data-testid="commerce-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-total-leads" icon={Users} label="Total leads" value={data.total_leads} sub={`${data.playbook_downloads} playbook leads`} />
        <Stat testid="stat-new-enquiries" icon={Inbox} label="New enquiries" value={data.new_enquiries} sub="Awaiting review" />
        <Stat testid="stat-consultations" icon={CalendarCheck} label="Consultations booked" value={data.consultations_booked} />
        <Stat testid="stat-open-proposals" icon={FileSignature} label="Open proposals" value={data.open_proposals} />
        <Stat testid="stat-active-clients" icon={UserCheck} label="Active clients" value={data.active_clients} />
        <Stat testid="stat-confirmed-revenue" icon={PoundSterling} label="Confirmed revenue" value={gbp(data.confirmed_revenue)} sub={`${data.payments_count} recorded payments`} />
        <Stat testid="stat-pending-invoices" icon={Receipt} label="Pending invoices" value={data.pending_invoices} sub={`${gbp(data.pending_invoices_amount)} outstanding`} />
        <Stat testid="stat-mrr" icon={Repeat} label="Monthly recurring" value={gbp(data.mrr)} sub="Active retainers paid in last 35 days" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Workflow pipeline" className="lg:col-span-2"><Funnel counts={data.stage_counts} total={data.total_leads} /></Panel>
        <div className="space-y-6">
          <Panel title="Conversion">
            <div className="p-5 grid grid-cols-2 gap-4" data-testid="commerce-conversion">
              <div><p className="text-xs text-slate-500">Lead → client</p><p className="text-xl font-bold">{pct(data.conversion_rate)}</p></div>
              <div><p className="text-xs text-slate-500">Lead → consultation+</p><p className="text-xl font-bold">{pct(data.consultation_rate)}</p></div>
            </div>
          </Panel>
          <Panel title="Acquisition sources"><Sources sources={data.sources} total={data.total_leads} /></Panel>
        </div>
      </div>
      <p className="text-xs text-slate-400">Revenue figures only include payments recorded against invoices (Stripe-confirmed or manually recorded). Proposal and deal values are never counted as revenue.</p>
    </div>
  );
};
